import { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { AuthProvider } from "./auth/AuthContext";
import { RequireAuth } from "./auth/RequireAuth";
import { usePendingInviteProcessor } from "./auth/usePendingInviteProcessor";
import { ToastProvider } from "./components/ToastProvider";
import { Spinner } from "./components/Spinner";

// ⚡ Bolt: Implemented Route-based Code Splitting
// 💡 What: Used React.lazy() and Suspense to dynamically import page components.
// 🎯 Why: Previously, all pages were bundled into a single large JS file (671 kB), increasing initial load time.
// 📊 Impact: Reduces main bundle size by splitting it into smaller chunks, improving initial page load time and TTI (Time to Interactive).
// 🔬 Measurement: Observe Vite build output - main chunk is smaller and separate chunks are generated for each page.

const LandingPage = lazy(() => import("./pages/LandingPage").then((m) => ({ default: m.LandingPage })));
const LoginPage = lazy(() => import("./pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const SignupPage = lazy(() => import("./pages/SignupPage").then((m) => ({ default: m.SignupPage })));
const DashboardPage = lazy(() => import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })));
const OrganizationPage = lazy(() => import("./pages/OrganizationPage").then((m) => ({ default: m.OrganizationPage })));
const AccountSettingsPage = lazy(() => import("./pages/AccountSettingsPage").then((m) => ({ default: m.AccountSettingsPage })));
const InviteRedemptionPage = lazy(() => import("./pages/InviteRedemptionPage").then((m) => ({ default: m.InviteRedemptionPage })));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage })));

function PendingInviteWatcher() {
  usePendingInviteProcessor();
  return null;
}

const PageFallback = () => (
  <div className="flex h-screen w-full items-center justify-center">
    <Spinner size={8} />
  </div>
);

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <PendingInviteWatcher />
        <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route
              path="/dashboard"
              element={
                <RequireAuth>
                  <DashboardPage />
                </RequireAuth>
              }
            />
            <Route
              path="/org/:orgId"
              element={
                <RequireAuth>
                  <OrganizationPage />
                </RequireAuth>
              }
            />
            <Route
              path="/account"
              element={
                <RequireAuth>
                  <AccountSettingsPage />
                </RequireAuth>
              }
            />
            <Route path="/invite" element={<InviteRedemptionPage />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </AuthProvider>
    </ToastProvider>
  );
}
